/**
 * tutorLivePageModes — les modes de tout l'écran que la page du live du masteur tient (pur), lus par
 * `<TutorLivePage>` ; testé à côté (Unistyles ne se charge pas sous Jest, la règle vit hors du rendu).
 *
 * **Un avis de la salle passe devant les modes** (`notice`) : la connexion qui s'établit ou se rétablit
 * porte son message et ses gestes (« Réessayer », « Annuler ») à la place de la salle ; la mosaïque prend cette
 * place-là, et l'avis disparaissait derrière elle — un masteur coupé en mosaïque ne voyait ni « Reconnexion… » ni
 * de quoi réessayer. L'ancien live montrait `ConnectingView` à la place de tout l'écran, quel que soit le mode.
 * Pendant un avis, la page montre donc la salle ; le mode choisi (`bar.mosaic.on`) reste celui de l'écran et revient
 * quand l'avis se lève. La colonne du champion ne s'agrandit plus (Wajih, 04/11) : ce qui se relit monte au centre.
 *
 * **Un bandeau ne tient rien** : « Aucun élève connecté… » et « Aucun élève inscrit… » se posent au-dessus de
 * la salle, qui reste là, et la mosaïque les porte aussi. Ils tenaient la salle comme la connexion : trois
 * minutes après le début d'une séance sans élève, la mosaïque choisie ne s'ouvrait plus (Wajih, live en prod
 * du 03/11 : « le bouton ne fait rien »).
 */
import { type UITutorLiveNotice } from "../../../ports/primary/ui/types/tutorLive"

export type TutorLivePageModesInput = {
    /** Le centre seul (une présentation sans recadrage) : pas de mosaïque. */
    centerOnly: boolean
    /** La mosaïque choisie dans la barre (`bar.mosaic.on`). */
    mosaicChosen: boolean
    /** L'avis à la place de la salle. */
    notice?: UITutorLiveNotice
}

export type TutorLivePageModes = {
    /** La connexion tient la salle : pas de mosaïque. */
    held: boolean
    /** La mosaïque est montrée. */
    mosaic: boolean
}

/**
 * Les avis qui prennent la place de la salle : la connexion qui s'établit ou se rétablit, et la question de la
 * caméra et du micro qui attend sa réponse (son message dit où répondre). Les bandeaux « Aucun élève… » se
 * posent au-dessus d'elle.
 */
export function holdsRoom(notice: UITutorLiveNotice | undefined): boolean {
    return notice === "connecting" || notice === "reconnecting" || notice === "media_pending"
}

export function tutorLivePageModes(input: TutorLivePageModesInput): TutorLivePageModes {
    const held = holdsRoom(input.notice)
    const free = !input.centerOnly && !held
    return { held, mosaic: free && input.mosaicChosen }
}
